import { useCallback } from 'react'
import type { JSX, KeyboardEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import clsx from 'clsx'

import { Button, Dialog, Flex } from '@buffer-mono/popcorn'

import { useDismissableBanner } from '~publish/hooks/useDismissableBanner'
import { useOnce } from '~publish/hooks/useOnce'

import { InsightsSnapshot } from '../InsightsSnapshot'
import type { InsightsSnapshotProps } from '../InsightsSnapshot'
import { usePersonalizedInsightsSnapshot } from './usePersonalizedInsightsSnapshot'
import { useInsightsAnnouncementTracking } from './useInsightsAnnouncementTracking'

import styles from './InsightsAnnouncementModalDialog.module.css'

const BANNER_ID = 'insights-launch-announcement-dialog'

/**
 * Accounts created on or after this date never see the announcement — it's an
 * introduction to the refreshed Insights for the existing user base, not for
 * users who arrive after launch.
 */
const LAUNCH_DATE = new Date('2026-07-13')

/** The announcement self-retires after this date. */
const EXPIRY_DATE = new Date('2026-08-31')

const INSIGHTS_ROUTE = '/insights'

const TABBABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Radix's own Tab wraparound only fires once focus is already on the first
 * or last tabbable control (see `@radix-ui/react-focus-scope`) — it never
 * accounts for focus sitting on the dialog root itself, which is where
 * `onOpenAutoFocus` below puts it. Without this, an immediate Shift+Tab
 * right after open goes unhandled and can escape the dialog.
 */
function focusLastTabbableElement(container: HTMLElement): void {
  const candidates = container.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR)
  candidates[candidates.length - 1]?.focus()
}

const HEADLINE = 'Curious how your content is doing?'
const DESCRIPTION =
  'See what’s working across every channel, and know exactly what to try next with our new Insights feature.'

/** Generic graph illustration shown in place of the metric grid on the no-data card. */
const FALLBACK_GRAPH_URL =
  'https://buffer-publish.s3.us-east-1.amazonaws.com/images/insights/insightsModalFallbackGraph.svg'

/**
 * No-data card: the branded snapshot chrome (header + footer) with a generic
 * graph illustration standing in for the metric grid.
 */
const FALLBACK_SNAPSHOT: InsightsSnapshotProps = {
  stats: [],
  dateRangeLabel: '',
  comparisonLabel: '',
  placeholderImageUrl: FALLBACK_GRAPH_URL,
}

export function InsightsAnnouncementModalDialog(): JSX.Element | null {
  const { isActive, dismiss } = useDismissableBanner(BANNER_ID, {
    accountSignUpBefore: LAUNCH_DATE,
    expiryDate: EXPIRY_DATE,
  })
  const navigate = useNavigate()
  const { trackDialogViewed, trackDialogDismissed } =
    useInsightsAnnouncementTracking()

  const { status, snapshot } = usePersonalizedInsightsSnapshot({
    skip: !isActive,
  })

  const variant = status === 'personalized' ? 'personalized' : 'fallback'

  useOnce(() => {
    // Track a view only once we know which variant resolved, so the tracked
    // variant is accurate.
    if (!isActive || status === 'loading') return false
    return trackDialogViewed(variant)
  }, [isActive, status, variant, trackDialogViewed])

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        trackDialogDismissed('close_button', variant)
        dismiss()
      }
    },
    [dismiss, trackDialogDismissed, variant],
  )

  const handleExplore = useCallback(() => {
    trackDialogDismissed('cta', variant)
    dismiss()
    navigate(INSIGHTS_ROUTE)
  }, [dismiss, navigate, trackDialogDismissed, variant])

  // Not targeted / dismissed, or still resolving which variant to show — stay
  // closed so the dialog opens already-classified (no content flash).
  if (!isActive || status === 'loading') {
    return null
  }

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <Dialog.Content
        size="large"
        className={styles.content}
        onOpenAutoFocus={(event): void => {
          // Skip Radix's default (focusing the close button) so nothing
          // looks pressed on a dialog nobody asked to open, but still move
          // focus onto the dialog itself — leaving it unfocused entirely
          // breaks Radix's Tab trap, letting focus reach the page behind.
          event.preventDefault()
          ;(event.currentTarget as HTMLElement).focus()
        }}
        onKeyDown={(event: KeyboardEvent<HTMLDivElement>): void => {
          if (event.key !== 'Tab' || !event.shiftKey) return
          if (event.currentTarget !== document.activeElement) return
          event.preventDefault()
          focusLastTabbableElement(event.currentTarget)
        }}
      >
        <Dialog.CloseButton
          className={styles.closeButton}
          onClick={(): void => trackDialogDismissed('close_button', variant)}
        />

        {/* Fanned deck of cards; the front card is the branded Insights
            snapshot — the same card the "Share as Post" flow generates. With no
            data, the metric grid is swapped for a generic graph illustration. */}
        <div className={clsx(styles.deck, !snapshot && styles.deckFallback)}>
          <div className={styles.cardBack} aria-hidden />
          <div className={styles.cardMid} aria-hidden />
          <div className={styles.cardFront}>
            <div className={styles.snapshotScale}>
              <InsightsSnapshot {...(snapshot ?? FALLBACK_SNAPSHOT)} compact />
            </div>
          </div>
        </div>

        <Flex direction="column" gap="xs" align="center">
          <Dialog.Title size="xl" className={styles.title}>
            {HEADLINE}
          </Dialog.Title>
          <Dialog.Description size="lg" className={styles.description}>
            {DESCRIPTION}
          </Dialog.Description>
        </Flex>

        <Button
          variant="primary"
          size="large"
          className={styles.ctaButton}
          onClick={handleExplore}
        >
          Discover Insights
        </Button>
      </Dialog.Content>
    </Dialog>
  )
}
